import { useEffect, useState } from 'react';
import { ExternalLink, Languages, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { TRANSLATION_TARGETS } from '@/lib/languages';
import { useDocumentTranslation } from '@/hooks/useDocumentTranslation';
import { SourceTranslationPane } from './SourceTranslationPane';

interface SourceUrlResponse {
  url: string;
  expires_in: number;
  file_name: string | null;
}

interface SourcePdfCardProps {
  jobId: string;
  /** Fired after a successful translate so parents can refresh activity feeds. */
  onTranslated?: () => void;
  className?: string;
}

/**
 * Source-document viewer: the original contract PDF plus an on-demand,
 * view-only translation of the whole document (full text + extracted info)
 * into a reviewer-chosen language, shown in a pane beside the PDF.
 *
 * The signed URL expires after 5 minutes — "Reload" regenerates it.
 * Translation posts to /api/v1/jobs/{id}/translate-document; the pipeline
 * never sees the result (agents always read the original source).
 */
export function SourcePdfCard({ jobId, onTranslated, className }: SourcePdfCardProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('en');
  // Signed URLs lapse (~5 min); once past the window the iframe silently 404s,
  // so flag it and prompt a reload instead of leaving a broken preview.
  const [expiresIn, setExpiresIn] = useState<number | null>(null);
  const [urlStale, setUrlStale] = useState(false);

  const {
    data: translation,
    loading: tLoading,
    error: tError,
    translate,
    reset,
  } = useDocumentTranslation(jobId, onTranslated);
  const showPane = Boolean(translation || tLoading || tError);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<SourceUrlResponse>(`/api/v1/jobs/${jobId}/source-url`);
      setUrl(resp.url);
      setFileName(resp.file_name);
      setExpiresIn(resp.expires_in);
      setUrlStale(false);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.message : 'Could not load source PDF.';
      setError(msg);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Warn ~15s before the signed URL actually lapses so the reviewer sees a
  // prompt rather than a silently-broken iframe. We don't auto-reload — that
  // would reset the reader's scroll position mid-read.
  useEffect(() => {
    if (!url || !expiresIn) return;
    const ms = Math.max(0, expiresIn - 15) * 1000;
    const timer = setTimeout(() => { setUrlStale(true); }, ms);
    return () => { clearTimeout(timer); };
  }, [url, expiresIn]);

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base">Source document</CardTitle>
            {fileName && (
              <p className="text-xs text-muted-foreground truncate mt-0.5" title={fileName}>
                {fileName}
              </p>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { void load(); }}
              disabled={loading}
              aria-label="Reload signed URL (5-minute expiry)"
              title="Reload signed URL"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} aria-hidden="true" />
            </Button>
            {url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                asChild
                aria-label="Open the PDF in a new tab"
                title="Open in new tab"
              >
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Translate control — pick a language and render the whole
            document + its extracted info in it, beside the PDF. */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Languages className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <label className="text-xs text-muted-foreground" htmlFor="doc-translate-target">
            Translate to:
          </label>
          <select
            id="doc-translate-target"
            className="rounded border bg-background px-2 py-1 text-xs"
            value={target}
            onChange={(e) => { setTarget(e.target.value); }}
            disabled={tLoading}
          >
            {TRANSLATION_TARGETS.map((t) => (
              <option key={t.code} value={t.code}>{t.label}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { void translate(target); }}
            disabled={tLoading}
            className="h-7 gap-1 text-xs"
          >
            {tLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Languages className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {translation ? 'Re-translate' : 'Translate'}
          </Button>
          {showPane && (
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="h-7 gap-1 text-xs"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Hide translation
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className={cn('grid gap-3', showPane && 'xl:grid-cols-2')}>
          <div>
            {loading && !url && (
              <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="text-sm">Loading signed URL…</span>
              </div>
            )}
            {error && <p role="alert" className="py-3 text-xs text-destructive">{error}</p>}
            {url && urlStale && (
              <div
                role="status"
                className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
              >
                <span>This preview link has expired.</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { void load(); }}
                  disabled={loading}
                  className="h-6 gap-1 text-xs"
                >
                  <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} aria-hidden="true" />
                  Reload
                </Button>
              </div>
            )}
            {url && (
              <iframe
                src={url}
                title={fileName ? `Original PDF — ${fileName}` : 'Original contract PDF'}
                className="h-[640px] w-full rounded-md border border-border bg-muted/20"
                // sandbox left unrestricted so PDF.js plug-in viewers can navigate.
              />
            )}
          </div>
          {showPane && (
            <SourceTranslationPane
              data={translation}
              loading={tLoading}
              error={tError}
              targetLanguage={target}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
